import { Amplify, Auth, Storage } from 'aws-amplify';
import { MutableRefObject } from 'react';

const amplifyConfigForDealershipUserPool = {
  Auth: {
    mandatorySignIn: true,
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
    ...(process.env.REACT_APP_COGNITO_ENDPOINT && {
      endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    }),
    authenticationFlowType: 'CUSTOM_AUTH',
  },
  Storage: {
    bucket: process.env.REACT_APP_S3_BUCKET_NAME,
    region: process.env.REACT_APP_COGNITO_REGION,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
  },
};

export const amplifyConfigForConsumerUserPool = {
  Auth: {
    mandatorySignIn: true,
    region: process.env.REACT_APP_CONSUMER_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_CONSUMER_COGNITO_USER_POOL_ID,
    identityPoolId: process.env.REACT_APP_CONSUMER_COGNITO_IDENTITY_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_CONSUMER_COGNITO_APP_CLIENT_ID,
    ...(process.env.REACT_APP_COGNITO_ENDPOINT && {
      endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    }),
    authenticationFlowType: 'CUSTOM_AUTH',
  },
  Storage: {
    bucket: process.env.REACT_APP_S3_BUCKET_NAME,
    region: process.env.REACT_APP_CONSUMER_COGNITO_REGION,
    identityPoolId: process.env.REACT_APP_CONSUMER_COGNITO_IDENTITY_POOL_ID,
  },
};

// Function to dynamically switch user pool configuration
export const configureConsumerAmplify = () => {
  Auth.configure(amplifyConfigForConsumerUserPool);
};

export function configureDealershipAmplify() {
  Amplify.configure(amplifyConfigForDealershipUserPool);
}

export async function verifyUserEmailIsLoggedIn(email: string) {
  const currentUser = await Auth.currentAuthenticatedUser();
  return currentUser.username === email;
}

function generateName(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface UploadFileConfig {
  bucket?: string;
  url?: string;
  path?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export enum StorageLevel {
  PRIVATE = 'private',
  PUBLIC = 'public',
  PROTECTED = 'protected',
}

export async function uploadFile(
  file: File,
  progressCallback?: (progress: any) => void,
  uploadOperationRef?: MutableRefObject<any>,
  config?: UploadFileConfig,
  level: StorageLevel = StorageLevel.PUBLIC,
): Promise<string> {
  const fileName = `${config?.path || ''}${generateName()}`;
  const bucket = config?.bucket || process.env.REACT_APP_S3_BUCKET_NAME;

  try {
    const existingFile = await Storage.get(fileName, {
      download: true,
      bucket,
      level,
    });
    if (existingFile) {
      // File already exist so trigger a new name
      return uploadFile(file, progressCallback, uploadOperationRef, config, level);
    }
  } catch {
    const uploadOperation = Storage.put(fileName, file, {
      progressCallback,
      contentType: file.type,
      bucket,
      level,
    });
    if (uploadOperationRef) {
      uploadOperationRef.current = uploadOperation;
    }
    const result = await uploadOperation;
    const middlePrefix = await getMiddlePrefix(level);
    return `${config?.url || process.env.REACT_APP_S3_URI}${middlePrefix}${result.key}`;
    // return `${process.env.REACT_APP_S3_URI}${result.key}`;
  }
  return '';
}

export async function cancelStorage(operation: any): Promise<boolean> {
  return await Storage.cancel(operation, 'my message for cancellation');
}

async function getMiddlePrefix(level: StorageLevel): Promise<string> {
  if (level === StorageLevel.PUBLIC) return '';
  const cognitoIdentityId = (await Auth.currentUserCredentials()).identityId;
  return cognitoIdentityId + '/';
}

export const getS3UriFromFileUrl = (fileUrl: string, isPrivate: boolean, bucketName?: string): string => {
  const s3Uri = fileUrl.replace(
    isPrivate ? process.env.REACT_APP_S3_PRIVATE_URI : process.env.REACT_APP_S3_URI || '',
    '',
  );
  const privatePart = isPrivate ? 'private' : 'public';
  return `s3://${bucketName || process.env.REACT_APP_S3_BUCKET_NAME}/${privatePart}/${s3Uri}`;
};

export interface DownloadFileResult {
  file: File;
  blob: Blob;
}

export async function downloadFileFromUrl(
  fileUrl: string,
  fileName: string,
  storageLevel: StorageLevel,
  identityId: string,
): Promise<DownloadFileResult> {
  try {
    // Extract just the filename (the last part after the last slash)
    const key = fileUrl.split('/').pop();

    if (!key) {
      throw new Error('Invalid file URL');
    }

    // Get the current credentials to ensure we're authenticated
    await Auth.currentCredentials();

    // Get the file content directly
    const result = await Storage.get(key, {
      level: storageLevel,
      download: true,
      bucket: process.env.REACT_APP_S3_BUCKET_NAME,
      identityId,
    });

    const blob = result.Body as Blob;

    // Create a File object from the blob
    const file = new File([blob], fileName, { type: blob.type });

    return { file, blob };
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
}

// Helper function to force download a blob
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const extractIdentityIdAndKeyFromUrl = (
  url: string,
  storageLevel: string,
): { identityId?: string; key: string } => {
  const path = url.split(`${storageLevel}/`)[1];

  if (storageLevel === 'public') {
    return { key: path };
  }

  const [identityId, ...keyParts] = path.split('/');
  return {
    identityId,
    key: keyParts.join('/'), // Handle cases where the key might contain additional '/' characters
  };
};
